import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Minus, Plus, Trash2, Calendar } from "lucide-react";
import { 
  type PDFElement, 
  type TextStyle, 
  type DateFormat,
  CURSIVE_FONTS, 
  DATE_FORMATS,
  formatDateWithFormat 
} from "./types";

interface ElementContextMenuProps {
  element: PDFElement;
  position: { x: number; y: number };
  containerRect: DOMRect | null;
  onUpdateStyle: (updates: Partial<TextStyle>) => void;
  onUpdateContent: (content: string) => void;
  onDelete: () => void;
}

export function ElementContextMenu({
  element,
  position,
  containerRect,
  onUpdateStyle,
  onUpdateContent,
  onDelete,
}: ElementContextMenuProps) {
  const isDate = element.type === 'date';
  const isText = element.type === 'text';
  
  if (!isDate && !isText) return null;

  // Calculate position relative to container, above the element
  const menuStyle: React.CSSProperties = {
    position: 'absolute',
    left: Math.max(8, Math.min(position.x, (containerRect?.width || 400) - 280)),
    top: Math.max(8, position.y - 52),
    zIndex: 100,
  };

  const currentFontSize = element.style?.fontSize || 16;

  const handleFontSizeChange = (delta: number) => {
    const newSize = Math.max(8, Math.min(72, currentFontSize + delta));
    onUpdateStyle({ fontSize: newSize });
  };

  const handleDateFormatChange = (format: DateFormat) => {
    const newContent = formatDateWithFormat(new Date(), format);
    onUpdateContent(newContent);
    onUpdateStyle({ dateFormat: format });
  };

  return (
    <div
      style={menuStyle}
      className="bg-popover border border-border rounded-lg shadow-lg p-1.5 flex items-center gap-1.5"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Font selector */}
      <Select
        value={element.style?.fontFamily || 'sans-serif'}
        onValueChange={(font) => onUpdateStyle({ fontFamily: font as TextStyle['fontFamily'] })}
      >
        <SelectTrigger className="w-[110px] h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CURSIVE_FONTS.map((font) => (
            <SelectItem key={font.value} value={font.value}>
              <span style={{ fontFamily: font.value }}>{font.label}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Font size controls */}
      <div className="flex items-center gap-0.5 border border-border rounded-md">
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          onClick={() => handleFontSizeChange(-2)}
        >
          <Minus className="w-3 h-3" />
        </Button>
        <span className="text-xs w-7 text-center tabular-nums">{currentFontSize}</span>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          onClick={() => handleFontSizeChange(2)}
        >
          <Plus className="w-3 h-3" />
        </Button>
      </div>

      {/* Date format selector */}
      {isDate && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" className="h-8 px-2 gap-1">
              <Calendar className="w-3 h-3" />
              <span className="text-xs">Format</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {DATE_FORMATS.map((format) => (
              <DropdownMenuItem
                key={format.value}
                onClick={() => handleDateFormatChange(format.value)}
              >
                <span className="text-sm">{format.example}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Delete button */}
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
        onClick={onDelete}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}
